import { useState, useEffect } from 'react';
import { GetData } from '../services/api';

/**
 * Custom hook to fetch and manage workflow variables
 * 
 * @returns {Object} { variables, loading, error, refresh }
 */
const useWorkflowVariables = () => {
  const [variables, setVariables] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVariables = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await GetData('/workflows/variables');
      setVariables(response.data);
    } catch (err) {
      console.error('Error fetching workflow variables:', err);
      setError(err.message || 'Error al cargar variables');
      // Set default variables if API fails (based on Customer model)
      setVariables({
        contactFields: {
          variables: {
            name: { description: 'Nombre del contacto', example: 'Juan', usage: '{{name}}' },
            lastName: { description: 'Apellido del contacto', example: 'Pérez', usage: '{{lastName}}' },
            email: { description: 'Email del contacto', example: 'juan@example.com', usage: '{{email}}' },
            phone: { description: 'Teléfono del contacto', example: '+1234567890', usage: '{{phone}}' },
            id: { description: 'ID único del contacto', example: '507f1f77bcf86cd799439011', usage: '{{id}}' },
            city: { description: 'Ciudad del contacto', example: 'Madrid', usage: '{{city}}' },
            address: { description: 'Dirección del contacto', example: 'Calle Principal 123', usage: '{{address}}' },
            state: { description: 'Estado/Provincia del contacto', example: 'Madrid', usage: '{{state}}' },
            postalCode: { description: 'Código postal del contacto', example: '28001', usage: '{{postalCode}}' },
            country: { description: 'País del contacto', example: 'España', usage: '{{country}}' },
            type: { description: 'Tipo de contacto', example: 'lead', usage: '{{type}}' },
            companyId: { description: 'ID de la compañía del contacto', example: '507f1f77bcf86cd799439011', usage: '{{companyId}}' },
            url: { description: 'URL del contacto (sitio web, perfil, etc.)', example: 'https://example.com', usage: '{{url}}' },
            tags: { description: 'Tags/etiquetas del contacto (array)', example: '["lead", "vip", "newsletter"]', usage: '{{tags}}' },
            referencia: { description: 'Referencia del contacto', example: 'REF-12345', usage: '{{referencia}}' },
            // Aliases para compatibilidad
            contactName: { description: 'Alias para name', example: 'Juan', usage: '{{contactName}}', note: 'Se mapea a name' },
            contactEmail: { description: 'Alias para email', example: 'juan@example.com', usage: '{{contactEmail}}', note: 'Se mapea a email' },
            contactPhone: { description: 'Alias para phone', example: '+1234567890', usage: '{{contactPhone}}', note: 'Se mapea a phone' },
            contactId: { description: 'Alias para id', example: '507f1f77bcf86cd799439011', usage: '{{contactId}}', note: 'Se mapea a id' },
          }
        }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVariables();
  }, []);

  return {
    variables,
    loading,
    error,
    refresh: fetchVariables,
  };
};

export default useWorkflowVariables;

